/**
 * useLLMConfig Hook
 * Manage LLM (OpenRouter) configuration with API integration
 */

import adminApi from '@/services/admin.api';
import { useEffect, useState } from 'react';
import { showPlanLockIfTagged } from '@/utils/plan-lock.util';

export interface LLMModelEntry {
  id: string;
  name: string;
  provider: string;
  isEnabled: boolean;
  deactivatedAt?: string;
  deactivatedReason?: string;
}

interface LLMConfig {
  apiKey: string;
  temperature: number;
  maxTokens: number;
  availableModels: LLMModelEntry[];
}

interface LLMUsage {
  tokensUsed: number;
  totalCost: number;
  requestCount: number;
  modelBreakdown: Array<{ model: string; tokens: number; cost: number }>;
}

export const useLLMConfig = () => {
  const [config, setConfig] = useState<LLMConfig>({
    apiKey: '',
    temperature: 0.7,
    maxTokens: 1000,
    availableModels: [],
  });

  const [usage, setUsage] = useState<LLMUsage>({
    tokensUsed: 0,
    totalCost: 0,
    requestCount: 0,
    modelBreakdown: [],
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Fetch current config on mount
  useEffect(() => {
    fetchConfig();
    fetchUsage();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const data = await adminApi.getLLMConfig();
      if (data) {
        setConfig({
          apiKey: data.apiKey || '',
          temperature: data.temperature ?? 0.7,
          maxTokens: data.maxTokens ?? 1000,
          availableModels: data.availableModels || [],
        });
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch configuration');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsage = async () => {
    try {
      const data = await adminApi.getLLMUsage();
      setUsage(data);
    } catch (err: any) {
      console.error('Failed to fetch usage:', err);
    }
  };

  const updateConfig = async (newConfig: Partial<Pick<LLMConfig, 'apiKey' | 'temperature' | 'maxTokens'>>) => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');

      await adminApi.updateLLMConfig(newConfig);

      setConfig({ ...config, ...newConfig });
      setSuccess('Configuration updated successfully!');

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      if (!showPlanLockIfTagged(err)) setError(err.message || 'Failed to update configuration');
    } finally {
      setLoading(false);
    }
  };

  const addModel = async (modelId: string) => {
    try {
      setError('');
      setSuccess('');
      const availableModels = await adminApi.addLLMModel(modelId);
      setConfig((prev) => ({ ...prev, availableModels }));
      setSuccess('Model added!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      if (!showPlanLockIfTagged(err)) setError(err.message || 'Failed to add model');
      throw err;
    }
  };

  const deleteModel = async (modelId: string) => {
    try {
      setError('');
      const availableModels = await adminApi.removeLLMModel(modelId);
      setConfig((prev) => ({ ...prev, availableModels }));
    } catch (err: any) {
      if (!showPlanLockIfTagged(err)) setError(err.message || 'Failed to remove model');
    }
  };

  return {
    config,
    usage,
    loading,
    error,
    success,
    updateConfig,
    addModel,
    deleteModel,
    refetch: fetchConfig,
    refetchUsage: fetchUsage,
  };
};
